// ------------------------------------------------------------------
// PURE MODULE: gallery_pinned_filmstrip.mod.js
// ------------------------------------------------------------------
import gsap from "gsap";

export function GalleryPinnedFilmstrip() {
    let instanceConfig = {};
    let galleryUnits = [];

    // Simple debounce utility to protect the main thread during window resize
    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    };

    return {
        init(customConfig = {}) {
            instanceConfig = { ...GalleryPinnedFilmstrip.defaults, ...customConfig };
            const galleries = document.querySelectorAll(instanceConfig.targets);

            galleryUnits = Array.from(galleries).map((galleryWrapper) => {
                const filmStrip = galleryWrapper.querySelector(instanceConfig.stripSelector);
                const slides = gsap.utils.toArray(galleryWrapper.querySelectorAll(instanceConfig.slideSelector));
                
                if (!filmStrip || slides.length === 0) return () => {};

                const slidecount = slides.length;
                const slideshift = slidecount - 1;
				
				
// =========================================================
// ACCESSIBILITY: REDUCED MOTION DISMANTLE
// =========================================================
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cameraLens = filmStrip.parentElement;

if (prefersReducedMotion) {
    galleryWrapper.style.height = 'auto';
    cameraLens.style.position = 'static';
    cameraLens.style.height = 'auto';
    cameraLens.style.overflow = 'visible';
    filmStrip.style.width = '100vw';
    filmStrip.style.flexDirection = 'column';
    filmStrip.style.height = 'auto';
    return () => {}; // Abort the GSAP build and return an empty render loop
}
                
                // =========================================================
                // LOCAL STATE & CACHE
                // =========================================================
                let isGalleryInView = false;
                let galleryStartOffset = 0;
                let scrollableDistance = 0;
                let lastActiveSlideIndex = -1;
                let lastGalProgress = -1;
                
                const lazyWrappers = slides.map(slide => slide.querySelector('.lazy-reveal-wrapper') || null);

                // =========================================================
                // MATH & RESIZE ENGINE
                // =========================================================
/*                 const calculateMetrics = () => {
                    const horizontalMoveDistance = filmStrip.scrollWidth - window.innerWidth;
                    galleryWrapper.style.height = `${horizontalMoveDistance + window.innerHeight}px`;
                    
                    const rect = galleryWrapper.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    galleryStartOffset = rect.top + scrollTop;
                    scrollableDistance = horizontalMoveDistance;
                }; */
				
	const calculateMetrics = () => {
    const rect = galleryWrapper.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    galleryStartOffset = rect.top + scrollTop;
    // Rely purely on the 300vh/400vh set in your CSS
    scrollableDistance = galleryWrapper.offsetHeight - window.innerHeight; 
};

                calculateMetrics();
                const resizeObserver = new ResizeObserver(debounce(calculateMetrics, 15));
                resizeObserver.observe(document.body);


// =========================================================
// ACCESSIBILITY: KEYBOARD TABBING SYNC
// =========================================================
document.addEventListener('focusin', (e) => {
    if (filmStrip.contains(e.target)) {
        const slideIndex = slides.findIndex(slide => slide.contains(e.target));
        if (slideIndex !== -1) {
            const targetY = galleryStartOffset + (slideIndex * window.innerWidth);
            // Ensure Lenis is globally accessible, otherwise fallback to native jump
            if (typeof lenis !== 'undefined') {
                lenis.scrollTo(targetY, { immediate: true });
            } else {
                window.scrollTo({ top: targetY, behavior: 'instant' });
            }
        }
    }
});
// =========================================================
// ACCESSIBILITY: PREVENT NATIVE SCROLL HIJACK
// =========================================================
cameraLens.addEventListener('scroll', () => {
    if (cameraLens.scrollLeft !== 0) {
        cameraLens.scrollLeft = 0;
    }
});

                // =========================================================
                // DYNAMIC GSAP BUILDER
                // =========================================================
                const tl = gsap.timeline({ paused: true });
                gsap.set(slides, { zIndex: (i) => i });
                
                tl.to(filmStrip, { xPercent: -(100 * slideshift / slidecount), ease: "none", duration: slideshift }, 0);
                
                slides.forEach((slide, i) => {
                    if (i < slides.length - 1) {
                        tl.to(slide, {
                            scale: 0.55,
                            opacity: 0,
                            xPercent: 100,
                            ease: "none",
                            duration: 1,
                            force3D: true
                        }, i);
                    }
                });

                // =========================================================
                // MASTER MANAGER IO HOOK
                // Requires 'data-common' on the gallery wrapper in HTML
                // =========================================================
                galleryWrapper._setVisibility = (visible) => { isGalleryInView = visible; };

                // =========================================================
                // THE ISOLATED RENDER LOOP
                // =========================================================
                return (currentScroll) => {
                    if (!isGalleryInView) return;

                    const rawProgress = (currentScroll - galleryStartOffset) / (scrollableDistance || 1);
                    const clampedProgress = Math.max(0, Math.min(1, rawProgress));
                    const quantizedProgress = Math.round(clampedProgress * 10000) / 10000;
                    
                    // Allow raw progress to dip below 0 or above max to accurately catch edge exits
                    const currentN_int = Math.floor(rawProgress * slideshift);

                    // --- VIP HANDSHAKE & OVERRIDE TOGGLES ---
                    if (currentN_int !== lastActiveSlideIndex) {
                        
                        // 1. Release previous slide's override
                        if (lastActiveSlideIndex >= 0 && lastActiveSlideIndex <= slideshift) {
                            if (lastActiveSlideIndex >= currentN_int && lazyWrappers[lastActiveSlideIndex]) {
                                lazyWrappers[lastActiveSlideIndex]._setOverride(false);
                            }
                        }
                        
                        // 2. Lock current slide's override
                        if (currentN_int >= 0 && currentN_int <= slideshift && lazyWrappers[currentN_int]) {
                            lazyWrappers[currentN_int]._setOverride(true);
                        }

                        // 3. Trigger LazyLoad VIP Lane (Clamp to prevent sending -1 or out-of-bounds index)
                        const safeIndex = Math.max(0, Math.min(currentN_int, slideshift));
                        galleryWrapper.setAttribute('data-active-index', safeIndex);

                        lastActiveSlideIndex = currentN_int;
                    }

                    // --- SCRUB TIMELINE ---
                    if (quantizedProgress !== lastGalProgress) {
                        tl.progress(quantizedProgress);
                        lastGalProgress = quantizedProgress;
                    }
                };
            });
        },

        // =========================================================
        // GLOBAL DISPATCHER
        // =========================================================
        update(currentScroll) {
            for (let i = 0; i < galleryUnits.length; i++) {
                galleryUnits[i](currentScroll);
            }
        }
    };
}

GalleryPinnedFilmstrip.defaults = {
    targets: ".pinned-gallery",
    stripSelector: "#film-strip",
    slideSelector: ".gallery-slide"
};